import { useCallback, useEffect, useRef, useState } from 'react';
import { parseDate } from '../../../utils/dateUtils';
import { formatDateFromDate } from '../../../utils/dateUtils';
import type { ScheduleInfo, SlotInfo } from '../../../types/schedule.types';

export function useScheduleCurrentDate(params: {
  activeTab: string | undefined;
  scheduleID: string | undefined;
  schedule: ScheduleInfo | null;
  slotList: SlotInfo[] | undefined;
  scheduleCalendarDataLoading: boolean;
  userLastUsedCalendarDate: any;
  setServerSyncedField: (key: string, value: string) => void;
}) {
  const {
    activeTab,
    scheduleID,
    schedule,
    slotList,
    scheduleCalendarDataLoading,
    userLastUsedCalendarDate,
    setServerSyncedField,
  } = params;

  // 初始为今天；scheduleID 就绪后由 useEffect 按 userLastUsedCalendarDate / scheduleStartDate / slot 覆盖
  const [currentDate, setCurrentDate] = useState<Date | null>(() => new Date());

  const [selectedYear, setSelectedYear] = useState(() => {
    const date = currentDate || new Date();
    return date.getFullYear();
  });
  const [selectedMonth, setSelectedMonth] = useState(() => {
    const date = currentDate || new Date();
    return date.getMonth() + 1;
  });

  const lastInitializedScheduleIDRef = useRef<string | null>(null);

  const getDateDisplayText = useCallback((): string => {
    return `${selectedYear} / ${String(selectedMonth).padStart(2, '0')}`;
  }, [selectedYear, selectedMonth]);

  // 当 scheduleID 变化时初始化 currentDate：userLastUsedCalendarDate > scheduleStartDate > 今天（新建拍摄计划）> 最新 slot
  useEffect(() => {
    if (activeTab !== 'schedule' || !scheduleID) return;
    if (lastInitializedScheduleIDRef.current !== scheduleID) {
      lastInitializedScheduleIDRef.current = null;
    }
    if (lastInitializedScheduleIDRef.current === scheduleID) return;

    const isNewSchedule = !schedule?.scheduleStartDate && !schedule?.scheduleEndDate;
    if (scheduleCalendarDataLoading && !isNewSchedule) return;

    const calendarDates = userLastUsedCalendarDate;
    if (Array.isArray(calendarDates) && calendarDates.length > 0) {
      const dateCache = calendarDates.find((item) => {
        const parts = String(item).split(';');
        return parts.length >= 2 && parts[0] === scheduleID;
      });
      if (dateCache) {
        const parts = String(dateCache).split(';');
        const dateStr = parts[1];
        try {
          const cachedDate = parseDate(dateStr);
          if (!isNaN(cachedDate.getTime())) {
            setCurrentDate(cachedDate);
            lastInitializedScheduleIDRef.current = scheduleID;
            return;
          }
        } catch {
          // ignore
        }
      }
    }

    const hasSlots = !!(slotList && slotList.length > 0);
    if (isNewSchedule || !hasSlots) {
      setCurrentDate(new Date());
      lastInitializedScheduleIDRef.current = scheduleID;
      return;
    }

    if (schedule?.scheduleStartDate) {
      try {
        const startDate = parseDate(schedule.scheduleStartDate);
        if (!isNaN(startDate.getTime())) {
          setCurrentDate(startDate);
          lastInitializedScheduleIDRef.current = scheduleID;
          return;
        }
      } catch {
        // ignore
      }
    }

    const sortedSlots = [...(slotList ?? [])].sort((a, b) => {
      const timeA = a.slotUpdateTime ? new Date(a.slotUpdateTime).getTime() : 0;
      const timeB = b.slotUpdateTime ? new Date(b.slotUpdateTime).getTime() : 0;
      return timeB - timeA;
    });
    const latestSlot = sortedSlots[0];
    if (latestSlot?.slotDate) {
      try {
        const dateStr = latestSlot.slotDate.split('T')[0];
        const slotDateObj = parseDate(dateStr);
        if (!isNaN(slotDateObj.getTime())) {
          setCurrentDate(slotDateObj);
        }
      } catch {
        setCurrentDate(new Date());
      }
    } else {
      setCurrentDate(new Date());
    }
    lastInitializedScheduleIDRef.current = scheduleID;
  }, [
    activeTab,
    scheduleID,
    schedule?.scheduleStartDate,
    schedule?.scheduleEndDate,
    slotList,
    scheduleCalendarDataLoading,
    userLastUsedCalendarDate,
  ]);

  // 当 currentDate 变化时，更新 selectedYear 和 selectedMonth
  useEffect(() => {
    if (currentDate) {
      setSelectedYear(currentDate.getFullYear());
      setSelectedMonth(currentDate.getMonth() + 1);
    }
  }, [currentDate]);

  const handleDateSelect = useCallback(
    (date: Date) => {
      setCurrentDate(date);
      if (scheduleID) {
        const dateStr = formatDateFromDate(date);
        setServerSyncedField('userLastUsedCalendarDate', `${scheduleID};${dateStr}`);
      }
    },
    [scheduleID, setServerSyncedField],
  );

  // scheduleID 对应的 currentDate 已按缓存 / 开机日期写入后才为 true。
  // 写入发生在 effect 里，紧接着的 setCurrentDate 会再渲染一次，此标记在那次渲染上变为 true。
  const currentDateReady =
    !!scheduleID && lastInitializedScheduleIDRef.current === scheduleID;

  return {
    currentDate,
    currentDateReady,
    setCurrentDate,
    selectedYear,
    setSelectedYear,
    selectedMonth,
    setSelectedMonth,
    handleDateSelect,
    getDateDisplayText,
  };
}

